import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Thumbs, Controller, Autoplay } from 'swiper/modules';
import lazysizes from 'lazysizes';

import VideoHelper from '../../00_utilities/helpers/VideoHelper';
import AccessibilityHelper from '../../00_utilities/helpers/AccessibilityHelper';
import GeneralHelper from '../../00_utilities/helpers/GeneralHelper';

import 'swiper/swiper.scss';
import 'swiper/modules/a11y.scss';
import 'swiper/modules/pagination.scss';
import 'swiper/modules/thumbs.scss';
import 'swiper/modules/controller.scss';

class Slider {
    constructor(elem) {
        this.elem = elem;
        this.breakpoints = {
            'screen-xs': 576,
            'screen-sm': 768,
            'screen-md': 992,
            'screen-lg': 1260,
            'screen-xl': 1440,
        };
        this.breakpoint = null;
        this.swiper = null;
        this.thumbs = null;
    }

    init(eventListeners = {}) {
        const swiper = this.elem.querySelector('.swiper');
        if (swiper.querySelectorAll('.swiper-slide').length > 1) {
            this.addBreakpoint();

            eventListeners.slideChange = () => {
                VideoHelper.stopAll();
                if (this.elem.classList.contains('slider--hero-audio')) {
                    const allAudios = this.elem.querySelectorAll('audio');
                    allAudios.forEach((item) => {
                        item.pause();
                    });
                }
            };

            eventListeners.init = (swiperInstance) => {
                const targetSlide = this.getUrlTargetParameter();
                if (targetSlide !== null && this.isCorrectSlider()) {
                    swiperInstance.slideTo(targetSlide);
                }
            };

            // if the hash has been changed (activation of an in-page link)
            window.addEventListener('hashchange', () => {
                const targetSlide = this.getUrlTargetParameter();
                if (targetSlide !== null && this.isCorrectSlider()) {
                    this.swiper.slideTo(targetSlide);
                }
            });

            // if an anchor link has been used
            window.addEventListener('anchorClicked', (event) => {
                const targetSlide = this.getUrlTargetParameter(event.detail.href);
                if (targetSlide !== null && this.isCorrectSlider(event.detail.targetId)) {
                    this.swiper.slideTo(targetSlide);
                }
            });

            if (this.elem.hasAttribute('data-thumb-slider')) {
                eventListeners.beforeInit = () => {
                    // initialize lazysizes as slider is not able to calculate correct 'jump marks'
                    const imagesToLoad = this.elem.querySelectorAll('.lazyload');
                    imagesToLoad.forEach((image) => {
                        lazysizes.loader.unveil(image);
                    });
                };
                eventListeners.snapGridLengthChange = () => {
                    if (this.swiper.snapGrid.length !== this.swiper.slidesGrid.length) {
                        this.swiper.snapGrid = this.swiper.slidesGrid.slice(0);
                    }
                };

                const thumbSlider = document.getElementById(this.elem.getAttribute('data-thumb-slider'));
                this.thumbs = this.initSlider(thumbSlider.querySelector('.swiper'), null, {
                    watchSlidesProgress: true,
                    freeMode: true,
                    slidesPerView: 6,
                    modules: [Thumbs],
                    breakpoints: {
                        [this.breakpoints['screen-xs']]: {
                            slidesPerView: 8,
                        },
                        [this.breakpoints['screen-sm']]: {
                            slidesPerView: 10,
                        },
                    },
                });
            }

            window.addEventListener('resize', () => this.breakpointChecker(eventListeners));
            this.breakpointChecker(eventListeners);
        }
    }

    /**
     * get parameter for target slide (example url .../mediathek/vorspiel#c4898?slide=3)
     * @returns {number|null}
     */
    getUrlTargetParameter(url = window.location.href) {
        const queryMatch = url.match(/\?slide=(\d+)/);

        return queryMatch ? parseInt(queryMatch[1], 10) - 1 : null;
    }

    /**
     * check if hash is equal to the slider element id
     */
    isCorrectSlider(url = window.location.hash) {
        const component = this.elem.closest('.component-container');
        const targetId = GeneralHelper.getTargetIdFromUrl(url) || url;
        return targetId === component.id;
    }

    initSlider(slider, eventListeners, settingsOverride = {}) {
        const settings = this.getSettings();
        this.swiper = new Swiper(slider, {
            ...{
                ...settings,
                ...settingsOverride,
            },
            on: eventListeners,
        });

        return this.swiper;
    }

    getSettings() {
        const settings = {
            a11y: true,
            slidesPerView: 1,
            spaceBetween: 0,
            loop: true,
            modules: [Navigation, Pagination, A11y],
        };

        let buttonPrev = this.elem.querySelector('.swiper-button--prev');
        let buttonNext = this.elem.querySelector('.swiper-button--next');

        const buttonPrevWrapper = this.elem.querySelector('.swiper-button-wrapper--prev');
        const buttonNextWrapper = this.elem.querySelector('.swiper-button-wrapper--next');

        if (buttonPrevWrapper) {
            buttonPrev = buttonPrevWrapper;
        }
        if (buttonNextWrapper) {
            buttonNext = buttonNextWrapper;
        }

        if (buttonPrev && buttonNext) {
            settings.navigation = {
                nextEl: buttonNext,
                prevEl: buttonPrev,
            };
        }

        if (this.elem.querySelector('.swiper-pagination')) {
            settings.pagination = {
                el: this.elem.querySelector('.swiper-pagination'),
                clickable: true,
            };
        }

        if (this.elem.classList.contains('slider--media-big')) {
            settings.slidesPerView = 'auto';
            settings.loop = false;
        }

        if (this.elem.classList.contains('slider--ensemble')) {
            settings.slidesPerView = 1.25;
            settings.spaceBetween = 22;
            settings.breakpoints = {
                [this.breakpoints['screen-sm']]: {
                    slidesPerView: 2.5,
                },
                [this.breakpoints['screen-md']]: {
                    slidesPerView: 3,
                },
            };
        }

        if (this.elem.classList.contains('slider--dates')) {
            const isInAccordion = this.elem.closest('.accordion__content');
            settings.loop = false;
            settings.breakpoints = {
                [this.breakpoints['screen-xs']]: {
                    slidesPerView: 2,
                },
                [this.breakpoints['screen-sm']]: {
                    slidesPerView: 3,
                },
                [this.breakpoints['screen-lg']]: {
                    slidesPerView: isInAccordion ? 3 : 4,
                },
                1500: {
                    slidesPerView: isInAccordion ? 3 : 5,
                },
            };
        }

        if (this.elem.classList.contains('slider--mediathek')) {
            settings.slidesPerView = 2;
            settings.spaceBetween = 20;
            settings.loop = false;
            settings.centeredSlides = false;
            settings.preventClicks = false;

            settings.breakpoints = {
                [this.breakpoints['screen-sm']]: {
                    slidesPerView: 'auto',
                },
            };
        }

        if (this.elem.classList.contains('slider--hero-audio')
            || this.elem.classList.contains('slider--hero-mediathek')) {
            settings.slidesPerView = 1;
            settings.spaceBetween = 0;
            settings.loop = false;
            settings.spaceBetween = 40;
            settings.centeredSlides = true;

            if (this.elem.classList.contains('slider--hero-mediathek')) {
                settings.modules = [Navigation, Pagination, A11y, Autoplay];
                settings.preventClicks = false;
                settings.autoplay = {
                    delay: 4000,
                    pauseOnMouseEnter: true,
                    disableOnInteraction: false,
                };
            }

            if (this.elem.classList.contains('slider--mediathek--suggestion')) {
                settings.slideToClickedSlide = true;
                settings.slidesPerView = 2;
                settings.spaceBetween = 20;
                settings.loop = false;
                settings.centeredSlides = false;
                settings.preventClicks = false;
            }

            if (this.elem.classList.contains('slider--hero-audio')) {
                const slides = this.elem.querySelectorAll('.swiper-slide');
                settings.pagination = {
                    el: this.elem.querySelector('.swiper-pagination'),
                    clickable: true,
                    renderBullet: (index) => {
                        return `<span class="audio-slider-dots__link swiper-pagination-bullet">
                                    ${slides[index].querySelector('h2').textContent}
                                </span>`;
                    },
                };
            }

            settings.breakpoints = {
                [this.breakpoints['screen-xs']]: {
                    slidesPerView: 3,
                    spaceBetween: 0,
                },
            };
        }

        if (this.elem.hasAttribute('data-thumb-slider')) {
            settings.loop = false;
            settings.slidesPerView = 'auto';
            settings.modules = [Navigation, Thumbs, Controller, Pagination];
            settings.pagination = {
                el: this.elem.querySelector('.swiper-pagination'),
                type: 'fraction',
            };
            settings.thumbs = {
                swiper: this.thumbs,
            };
        }

        if (this.elem.hasAttribute('data-autoplay')) {
            settings.modules = [Navigation, Pagination, A11y, Autoplay];
            settings.autoplay = {
                delay: 6000,
                pauseOnMouseEnter: true,
                disableOnInteraction: false,
            };
        }

        AccessibilityHelper.reduceMotion(({ matches }) => {
            if (matches) {
                settings.autoplay = false;
            }
        });

        return settings;
    }

    addBreakpoint() {
        if (this.elem.classList.contains('slider--mediathek-clear-mobile')) {
            this.breakpoint = window.matchMedia(`(max-width: ${this.breakpoints['screen-xs'] - 1}px)`);
            return this.breakpoint;
        }

        this.breakpoint = window.matchMedia(null);
        return this.breakpoint;
    }

    breakpointChecker(eventListeners) {
        if (this.breakpoint.matches === true) {
            if (this.swiper !== null) this.swiper.destroy(true, true);
        } else if (this.breakpoint.matches === false) {
            this.initSlider(this.elem.querySelector('.swiper'), eventListeners);
        }
    }
}

export default Slider;
